import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip
} from '@mui/material'
import moment from 'moment-jalaali'

interface AdminInvoiceData {
  id: number
  plan: {
    title: string
  } | null
  user: {
    id: number
    first_name: string
    last_name: string
    email: string
  }
  price: number
  created_at: number
  status: 'success' | 'failed' | 'payment_pending'
}

type Order = 'asc' | 'desc'
type SortableKeys = 'created_at' | 'status' | 'price'

interface InvoicesTabProps {
  apiFetch: (url: string, options?: RequestInit) => Promise<any>
  handleApiCall: (apiCall: Promise<any>, successMessage: string) => Promise<any>
  showConfirmationModal: (message: string, onConfirm: () => void) => void
}

const InvoicesTab: React.FC<InvoicesTabProps> = ({ apiFetch, handleApiCall, showConfirmationModal }) => {
  const [invoices, setInvoices] = useState<AdminInvoiceData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [totalInvoices, setTotalInvoices] = useState(0)

  const [order, setOrder] = useState<Order>('desc')
  const [orderBy, setOrderBy] = useState<SortableKeys>('created_at')

  const fetchInvoices = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiFetch(`/admin/invoices?page=${page + 1}&size=${rowsPerPage}`)
      setInvoices(response.items || [])
      setTotalInvoices(response.total || 0)
    } catch (err: any) {
      setError(`خطا در بارگذاری صورت‌حساب‌ها: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }, [apiFetch, page, rowsPerPage])

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSortRequest = (property: SortableKeys) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const sortedInvoices = useMemo(() => {
    const comparator = (a: AdminInvoiceData, b: AdminInvoiceData): number => {
      let valA = a[orderBy]
      let valB = b[orderBy]
      if (valB < valA) return -1
      if (valB > valA) return 1
      return 0
    }
    const stabilizedThis = invoices.map((el, index) => [el, index] as [AdminInvoiceData, number])
    stabilizedThis.sort((a, b) => {
      const cmp = comparator(a[0], b[0])
      if (cmp !== 0) return cmp
      return a[1] - b[1]
    })
    if (order === 'desc') {
      return stabilizedThis.map(el => el[0]).reverse()
    }
    return stabilizedThis.map(el => el[0])
  }, [invoices, order, orderBy])

  const handleMarkAsPaid = useCallback(
    (invoiceId: number) => {
      showConfirmationModal(
        `آیا از علامت زدن این صورتحساب (ID: ${invoiceId}) به عنوان پرداخت شده اطمینان دارید؟`,
        () => {
          handleApiCall(
            apiFetch(`/admin/invoices/${invoiceId}/paid`, { method: 'POST' }),
            'صورتحساب با موفقیت به عنوان پرداخت شده علامت‌گذاری شد.'
          )
            .then(() => {
              fetchInvoices()
            })
            .catch(err => {
              console.error(err)
            })
        }
      )
    },
    [apiFetch, handleApiCall, showConfirmationModal, fetchInvoices]
  )

  const renderStatus = (status: 'success' | 'failed' | 'payment_pending') => {
    switch (status) {
      case 'success':
        return <Chip label='موفق' color='success' size='small' />
      case 'failed':
        return <Chip label='ناموفق' color='error' size='small' />
      case 'payment_pending':
        return <Chip label='در انتظار پرداخت' color='warning' size='small'/>
      default:
        return <Chip label={status} size='small' />
    }
  }

  if (loading && invoices.length === 0)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )

  return (
    <Paper className='management-card' sx={{ p: 0, overflow: 'hidden' }}>
      <Typography variant='h5' sx={{ p: 2 }}>
        لیست صورت‌حساب‌ها
      </Typography>
      {error && (
        <Alert severity='error' sx={{ m: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <CircularProgress sx={{ m: 2 }} size={20} />}

      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>کاربر</TableCell>
              <TableCell dir='rtl'>پلن</TableCell>
              <TableCell align='center' sortDirection={orderBy === 'price' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'price'}
                  direction={orderBy === 'price' ? order : 'asc'}
                  onClick={() => handleSortRequest('price')}
                >
                  مبلغ
                </TableSortLabel>
              </TableCell>
              <TableCell align='center' sortDirection={orderBy === 'created_at' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleSortRequest('created_at')}
                >
                  تاریخ
                </TableSortLabel>
              </TableCell>
              <TableCell align='center' sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSortRequest('status')}
                >
                  وضعیت
                </TableSortLabel>
              </TableCell>
              <TableCell align='center'>عملیات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedInvoices.map(invoice => (
              <TableRow hover key={invoice.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell>{invoice.id}</TableCell>
                <TableCell>
                  <Typography variant='body2' noWrap>
                    {invoice.user.first_name || ''} {invoice.user.last_name || ''}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' noWrap>
                    {invoice.user.email}
                  </Typography>
                </TableCell>
                <TableCell dir='rtl'>
                  {invoice.plan?.title || '---'}
                  {invoice.price === 0 && (
                    <Chip label='دمو' color='info' size='small' sx={{ mr: 1, ml: 1 }} />
                  )}
                </TableCell>
                <TableCell align='center'>${invoice.price / 100}</TableCell>
                <TableCell align='center'>
                  <Typography variant='body2' noWrap>
                    {moment(invoice.created_at * 1000).format('jYYYY/jMM/jDD HH:mm')}
                  </Typography>
                </TableCell>
                <TableCell align='center'>{renderStatus(invoice.status)}</TableCell>
                <TableCell align='center'>
                  {invoice.status === 'payment_pending' && (
                    <Button
                      variant='contained'
                      style={{ backgroundColor: 'var(--success-color)', color: '#fff' }}
                      size='small'
                      onClick={() => handleMarkAsPaid(invoice.id)}
                    >
                      تایید پرداخت
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component='div'
        count={totalInvoices}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage='تعداد در صفحه:'
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} از ${count !== -1 ? count : `بیشتر از ${to}`}`}
      />
    </Paper>
  )
}

export default InvoicesTab
